import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { 
  Card, 
  CardContent, 
  CardDescription, 
  CardHeader, 
  CardTitle 
} from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { 
  Mail, 
  Server, 
  Shield, 
  CheckCircle, 
  AlertTriangle,
  Save,
  TestTube,
  Eye,
  EyeOff
} from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const EmailConfigPage = () => {
  const [emailConfig, setEmailConfig] = useState({
    smtp_server: 'smtp.gmail.com',
    smtp_port: '587',
    smtp_username: '',
    smtp_password: '',
    sender_name: 'GridProfile',
    sender_email: '',
    use_tls: true,
    use_ssl: false
  });
  
  const [loading, setLoading] = useState(false);
  const [testing, setTesting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [testEmail, setTestEmail] = useState('');

  useEffect(() => {
    loadEmailConfig();
  }, []);

  const loadEmailConfig = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/config/email`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data) {
        setEmailConfig({
          ...emailConfig,
          ...response.data,
          smtp_password: '' // Por segurança, não carregar senha
        });
      }
    } catch (error) {
      console.error('Erro ao carregar configuração:', error);
      if (error.response?.status !== 404) {
        toast.error('Erro ao carregar configuração de e-mail');
      }
    }
  };

  const handleSave = async () => {
    if (!emailConfig.smtp_server || !emailConfig.smtp_username) {
      toast.error('Preencha pelo menos o servidor SMTP e usuário');
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/config/email`, emailConfig, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Configuração de e-mail salva com sucesso!');
    } catch (error) {
      console.error('Erro ao salvar configuração:', error);
      toast.error(error.response?.data?.detail || 'Erro ao salvar configuração');
    } finally {
      setLoading(false);
    }
  };

  const handleTest = async () => {
    if (!testEmail) {
      toast.error('Digite um e-mail para teste');
      return;
    }

    if (!emailConfig.smtp_server || !emailConfig.smtp_username) {
      toast.error('Configure primeiro o servidor SMTP');
      return;
    }

    setTesting(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/config/email/test`, {
        to_email: testEmail,
        config: emailConfig
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.success) {
        toast.success('E-mail de teste enviado com sucesso!');
      } else {
        toast.error('Falha no envio: ' + response.data.message);
      }
    } catch (error) {
      console.error('Erro no teste:', error);
      toast.error(error.response?.data?.detail || 'Erro ao testar configuração');
    } finally {
      setTesting(false);
    }
  };

  const presetConfigs = {
    gmail: {
      smtp_server: 'smtp.gmail.com',
      smtp_port: '587',
      use_tls: true,
      use_ssl: false
    },
    outlook: {
      smtp_server: 'smtp-mail.outlook.com',
      smtp_port: '587',
      use_tls: true,
      use_ssl: false
    },
    yahoo: {
      smtp_server: 'smtp.mail.yahoo.com',
      smtp_port: '587',
      use_tls: true,
      use_ssl: false
    }
  };

  const applyPreset = (preset) => {
    setEmailConfig({
      ...emailConfig,
      ...presetConfigs[preset]
    });
    toast.success(`Configuração ${preset.toUpperCase()} aplicada`);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-slate-800 flex items-center space-x-2">
            <Mail className="h-8 w-8 text-blue-600" />
            <span>Configuração de E-mail</span>
          </h1>
          <p className="text-slate-600 mt-1">Configure o servidor SMTP para envio de e-mails de assinatura eletrônica</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Configurações Principais */}
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle className="flex items-center space-x-2">
              <Server className="h-5 w-5" />
              <span>Configurações SMTP</span>
            </CardTitle>
            <CardDescription>
              Configure o servidor de e-mail para envio automático
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Configurações Rápidas */}
            <div>
              <Label className="text-sm font-medium">Configurações Rápidas</Label>
              <div className="flex space-x-2 mt-2">
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => applyPreset('gmail')}
                >
                  Gmail
                </Button>
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => applyPreset('outlook')}
                >
                  Outlook
                </Button>
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => applyPreset('yahoo')}
                >
                  Yahoo
                </Button>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="smtp_server">Servidor SMTP *</Label>
                <Input
                  id="smtp_server"
                  value={emailConfig.smtp_server}
                  onChange={(e) => setEmailConfig({...emailConfig, smtp_server: e.target.value})}
                  placeholder="smtp.gmail.com"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="smtp_port">Porta *</Label>
                <Input
                  id="smtp_port"
                  value={emailConfig.smtp_port}
                  onChange={(e) => setEmailConfig({...emailConfig, smtp_port: e.target.value})}
                  placeholder="587"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="smtp_username">Usuário/E-mail *</Label>
                <Input
                  id="smtp_username"
                  type="email"
                  value={emailConfig.smtp_username}
                  onChange={(e) => setEmailConfig({...emailConfig, smtp_username: e.target.value})}
                  placeholder="seu-email@gmail.com"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="smtp_password">Senha/Token *</Label>
                <div className="relative">
                  <Input
                    id="smtp_password"
                    type={showPassword ? "text" : "password"}
                    value={emailConfig.smtp_password}
                    onChange={(e) => setEmailConfig({...emailConfig, smtp_password: e.target.value})}
                    placeholder="Senha ou token de app"
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="absolute right-2 top-1/2 transform -translate-y-1/2 h-6 w-6 p-0"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? <EyeOff className="h-3 w-3" /> : <Eye className="h-3 w-3" />}
                  </Button>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="sender_name">Nome do Remetente</Label>
                <Input
                  id="sender_name"
                  value={emailConfig.sender_name}
                  onChange={(e) => setEmailConfig({...emailConfig, sender_name: e.target.value})}
                  placeholder="GridProfile"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="sender_email">E-mail do Remetente</Label>
                <Input
                  id="sender_email"
                  type="email"
                  value={emailConfig.sender_email}
                  onChange={(e) => setEmailConfig({...emailConfig, sender_email: e.target.value})}
                  placeholder="noreply@gridprofile.com"
                />
              </div>
            </div>

            <div className="flex items-center space-x-4">
              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  id="use_tls"
                  checked={emailConfig.use_tls}
                  onChange={(e) => setEmailConfig({...emailConfig, use_tls: e.target.checked})}
                />
                <Label htmlFor="use_tls">Usar TLS</Label>
              </div>
              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  id="use_ssl"
                  checked={emailConfig.use_ssl}
                  onChange={(e) => setEmailConfig({...emailConfig, use_ssl: e.target.checked})}
                />
                <Label htmlFor="use_ssl">Usar SSL</Label>
              </div>
            </div>

            <div className="flex space-x-3 pt-4">
              <Button 
                onClick={handleSave} 
                disabled={loading}
                className="flex items-center space-x-2"
              >
                <Save className="h-4 w-4" />
                <span>{loading ? 'Salvando...' : 'Salvar Configuração'}</span>
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Teste e Status */}
        <div className="space-y-6">
          {/* Teste de E-mail */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <TestTube className="h-5 w-5" />
                <span>Teste de E-mail</span>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="test_email">E-mail para teste</Label>
                <Input
                  id="test_email"
                  type="email"
                  value={testEmail}
                  onChange={(e) => setTestEmail(e.target.value)}
                  placeholder="teste@exemplo.com"
                />
              </div>
              <Button 
                onClick={handleTest}
                disabled={testing}
                variant="outline"
                className="w-full"
              >
                <TestTube className="h-4 w-4 mr-2" />
                {testing ? 'Enviando...' : 'Enviar Teste'}
              </Button>
            </CardContent>
          </Card>

          {/* Status */}
          <Card>
            <CardHeader>
              <CardTitle>Status</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-sm">Servidor</span>
                <Badge variant={emailConfig.smtp_server ? "default" : "secondary"}>
                  {emailConfig.smtp_server ? 'Configurado' : 'Não configurado'}
                </Badge>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm">Autenticação</span>
                <Badge variant={emailConfig.smtp_username && emailConfig.smtp_password ? "default" : "secondary"}>
                  {emailConfig.smtp_username && emailConfig.smtp_password ? 'Configurada' : 'Pendente'}
                </Badge>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm">Segurança</span>
                <Badge variant={emailConfig.use_tls || emailConfig.use_ssl ? "default" : "destructive"}>
                  {emailConfig.use_tls || emailConfig.use_ssl ? 'TLS/SSL' : 'Inseguro'}
                </Badge>
              </div>
            </CardContent>
          </Card>

          {/* Ajuda */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <Shield className="h-5 w-5" />
                <span>Ajuda - Gmail</span>
              </CardTitle>
            </CardHeader>
            <CardContent className="text-sm space-y-2">
              <p className="text-gray-600">Para usar Gmail:</p>
              <ol className="list-decimal list-inside space-y-1 text-gray-600">
                <li>Ative autenticação de 2 fatores</li>
                <li>Gere uma "Senha de app"</li>
                <li>Use a senha de app no campo senha</li>
                <li>Configure TLS na porta 587</li>
              </ol>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default EmailConfigPage;